import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Settings, Plus, Send, Clock, Activity, User, Mail, Key, Shield, Users, LogIn, LogOut, Calendar, FileText, UserPlus, Trash2, Edit, ChevronRight, DollarSign, Lock, Unlock, ShieldOff, Grid3x3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const AVAILABLE_MODULES = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'entreprise', label: 'Entreprise' },
  { id: 'inbox', label: 'Boîte de réception' },
  { id: 'prospects', label: 'Prospects' },
  { id: 'clients', label: 'Clients' },
  { id: 'contrats', label: 'Contrats' },
  { id: 'paiements', label: 'Paiements' },
  { id: 'suivi', label: 'Suivi' },
  { id: 'retours-clients', label: 'Retours clients' },
  { id: 'planning', label: 'Planning' },
  { id: 'tarifs', label: 'Tarifs' },
  { id: 'calendrier', label: 'Calendrier' },
  { id: 'echange', label: 'Messagerie' },
  { id: 'emplacement', label: 'Emplacement' },
  { id: 'panneaux', label: 'Nos Panneaux' },
  { id: 'technique', label: 'Technique' },
  { id: 'conception', label: 'Conception' },
  { id: 'saisie-commande', label: 'Saisie de commande' },
  { id: 'fournisseurs', label: 'Fournisseurs' },
  { id: 'utilisateurs', label: 'Utilisateurs' },
];
const MODULE_ALIASES = { messagerie: 'echange', 'nos-panneaux': 'panneaux' };

function ModuleChecklist({ selected, onChange, prefix = 'module-check' }) {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const all = selected.length === AVAILABLE_MODULES.length;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500">{selected.length}/{AVAILABLE_MODULES.length} module(s) · Admin exclu</span>
        <button
          type="button"
          onClick={() => onChange(all ? [] : AVAILABLE_MODULES.map((m) => m.id))}
          className="text-xs font-semibold text-blue-700 hover:text-blue-900"
          data-testid={`${prefix}-all`}
        >
          {all ? 'Tout désélectionner' : 'Tout sélectionner'}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
        {AVAILABLE_MODULES.map((m) => {
          const checked = selected.includes(m.id);
          return (
            <label
              key={m.id}
              className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm border transition-colors ${checked ? 'bg-purple-50 border-purple-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
              data-testid={`${prefix}-${m.id}`}
            >
              <Checkbox checked={checked} onCheckedChange={() => toggle(m.id)} className="h-4 w-4" />
              <span className="flex-1 text-slate-800">{m.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function Admin({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEmployeeStatus, setShowEmployeeStatus] = useState(false);
  const [showUsersListModal, setShowUsersListModal] = useState(false);
  const [showAccountsManagement, setShowAccountsManagement] = useState(false);
  const [employeesWithAccounts, setEmployeesWithAccounts] = useState([]);
  const [employeesStatus, setEmployeesStatus] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeActivity, setEmployeeActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedCredentials, setSelectedCredentials] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [newUser, setNewUser] = useState({
    employeId: '',
    email: '',
    password: '',
    role: 'employee'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, employesRes, logsRes] = await Promise.all([
        axios.get('/admin/users'),
        axios.get('/employes'),
        axios.get('/admin/activity-logs')
      ]);
      setUsers(usersRes.data);
      setEmployes(employesRes.data);
      setActivityLogs(logsRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Accès refusé. Seul l\'administrateur peut accéder à cette page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeesStatus = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/status`);
      setEmployeesStatus(response.data);
    } catch (error) {
      console.error('Error loading employees status:', error);
      toast.error('Erreur lors du chargement des statuts');
    }
  };

  const loadEmployeeActivity = async (employeeId) => {
    setLoadingActivity(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/${employeeId}/activity`);
      setEmployeeActivity(response.data);
    } catch (error) {
      console.error('Error loading employee activity:', error);
      toast.error('Erreur lors du chargement de l\'activité');
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleEmployeeClick = async (employee) => {
    setSelectedEmployee(employee);
    await loadEmployeeActivity(employee.id);
  };

  const handleToggleEmployeeStatus = async () => {
    const newStatus = !showEmployeeStatus;
    setShowEmployeeStatus(newStatus);
    if (newStatus) {
      await loadEmployeesStatus();
    } else {
      setSelectedEmployee(null);
      setEmployeeActivity(null);
    }
  };

  const handleShowUsersList = async () => {
    setShowUsersListModal(true);
    await loadEmployeesStatus();
  };

  const loadEmployeesWithAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/with-accounts`);
      setEmployeesWithAccounts(response.data);
    } catch (error) {
      console.error('Error loading employees with accounts:', error);
      toast.error('Erreur lors du chargement des comptes');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleToggleAccountsManagement = async () => {
    const newStatus = !showAccountsManagement;
    setShowAccountsManagement(newStatus);
    if (newStatus) {
      await loadEmployeesWithAccounts();
    }
  };

  const handleCreateAccount = async (employeId) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/${employeId}/create-account`);
      const { credentials } = response.data;
      
      // Show credentials in modal
      setSelectedCredentials(credentials);
      setShowCredentialsModal(true);
      
      toast.success('Compte créé avec succès !');
      
      // Reload the list
      await loadEmployeesWithAccounts();
    } catch (error) {
      console.error('Error creating account:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    }
  };

  const handleResetPassword = async (employeId) => {
    if (!window.confirm('Voulez-vous vraiment réinitialiser le mot de passe de cet employé ?')) {
      return;
    }
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/${employeId}/reset-password`);
      const { credentials } = response.data;
      
      // Show new credentials in modal
      setSelectedCredentials(credentials);
      setShowCredentialsModal(true);
      
      toast.success('Mot de passe réinitialisé !');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  const handleDeleteAccount = async (employeId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce compte ? L\'employé ne pourra plus se connecter.')) {
      return;
    }
    
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/${employeId}/delete-account`);
      toast.success('Compte supprimé avec succès');
      await loadEmployeesWithAccounts();
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // ─── Nouveaux handlers : blocage / modules / force logout ───────
  const handleToggleBlock = async (userId, currentlyBlocked) => {
    if (!userId) { toast.error('Compte utilisateur introuvable'); return; }
    const label = currentlyBlocked ? 'débloquer' : 'bloquer';
    if (!window.confirm(`Voulez-vous vraiment ${label} ce compte ?`)) return;
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/toggle-block`);
      toast.success(res.data.is_blocked ? 'Compte bloqué — connexion refusée' : 'Compte débloqué');
      await loadEmployeesWithAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur blocage');
    }
  };

  const handleForceLogout = async (userId) => {
    if (!userId) { toast.error('Compte utilisateur introuvable'); return; }
    if (!window.confirm('Forcer la déconnexion de toutes les sessions de cet utilisateur ?')) return;
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/force-logout`);
      toast.success('Sessions invalidées — l\'utilisateur devra se reconnecter');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  // Force la migration des 2 emplacements déroulants manquants (utile après un déploiement)
  const handleForceSeedEmplacements = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/seed-emplacements`);
      const { created, existing, details } = res.data || {};
      if (created > 0) {
        toast.success(`${created} emplacement${created > 1 ? 's' : ''} ajouté${created > 1 ? 's' : ''} · ${existing} déjà présent${existing > 1 ? 's' : ''}`);
      } else if (existing >= 2) {
        toast.success('Les 10 emplacements déroulants sont déjà présents ✓');
      } else {
        toast.info((details || []).join(' · ') || 'Aucun changement');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la synchronisation');
    }
  };

  // Supprimer un utilisateur depuis la carte "État de connexion" (bouton rouge)
  const handleDeleteUserFromStatus = async (emp) => {
    // emp.user_id peut être null si employé sans compte : on essaie de trouver son user via email
    let userId = emp.user_id;
    if (!userId && emp.email) {
      try {
        const usersRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`);
        const match = (usersRes.data || []).find(u => u.email === emp.email);
        if (match) userId = match.id;
      } catch { /* ignore */ }
    }
    if (!userId) { toast.error('Aucun compte utilisateur associé à supprimer'); return; }
    if (!window.confirm(`Supprimer définitivement le compte de ${emp.name || emp.email} ?\nCette action est irréversible.`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`);
      toast.success('Compte supprimé');
      // Refresh les 2 listes
      await Promise.all([loadEmployeesWithAccounts(), loadData()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const [showDefineModulesPicker, setShowDefineModulesPicker] = useState(false);

  const openDefineModulesModal = async () => {
    setShowDefineModulesPicker(true);
    if (employeesWithAccounts.length === 0) {
      await loadEmployeesWithAccounts();
    }
  };

  const pickEmployeeForModules = (emp) => {
    setShowDefineModulesPicker(false);
    openModulesModal(emp);
  };

  const [modulesUserId, setModulesUserId] = useState(null);
  const [modulesUserEmail, setModulesUserEmail] = useState('');
  const [modulesSelection, setModulesSelection] = useState([]);
  const [modulesSaving, setModulesSaving] = useState(false);

  const openModulesModal = (emp) => {
    if (!emp.user_id) { toast.error('Aucun compte utilisateur pour cet employé'); return; }
    setModulesUserId(emp.user_id);
    setModulesUserEmail(emp.email || '');
    setModulesSelection(Array.isArray(emp.modules_allowed) ? [...new Set(emp.modules_allowed.map((m) => MODULE_ALIASES[m] || m))].filter((m) => AVAILABLE_MODULES.some((x) => x.id === m)) : []);
  };

  const saveModules = async () => {
    setModulesSaving(true);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${modulesUserId}/modules`, {
        modules: modulesSelection,
      });
      toast.success('Modules autorisés mis à jour');
      setModulesUserId(null);
      await loadEmployeesWithAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur mise à jour modules');
    } finally {
      setModulesSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier !');
  };

  const formatDailyConnectionTime = (minutes) => {
    if (!minutes || minutes === 0) return "Pas de connexion aujourd'hui";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m aujourd'hui`;
    }
    return `${mins}m aujourd'hui`;
  };

  const handleCreateUser = async () => {
    if (!newUser.employeId || !newUser.email || !newUser.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        email: newUser.email,
        password: newUser.password,
        name: newUser.employeId, // Using employeId field as name
        role: newUser.role,
        modules: newUser.role === 'admin' ? [] : (newUser.modules || []),
      });
      
      const roleLabel = newUser.role === 'admin' ? 'Administrateur' : 
                        newUser.role === 'investisseur' ? 'Investisseur' : 'Employé';
      toast.success(`Identifiant ${roleLabel} créé pour ${newUser.employeId}`);
      setShowCreateModal(false);
      setNewUser({
        employeId: '',
        email: '',
        password: '',
        role: 'employee'
      });
      // Rafraîchit les 2 listes + ouvre la section "Gestion des Comptes"
      // pour que le nouvel identifiant soit immédiatement visible et actionnable.
      await Promise.all([loadData(), loadEmployeesWithAccounts()]);
      setShowAccountsManagement(true);
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleSendCredentials = async (employeId) => {
    if (!confirm('Voulez-vous envoyer les identifiants de connexion par email à cet employé ?')) {
      return;
    }

    try {
      const response = await axios.post(`/admin/employees/${employeId}/send-credentials`);
      toast.success(response.data.message || 'Email envoyé avec succès !');
      
      // Log activity
      await loadEmployeesWithAccounts();
    } catch (error) {
      console.error('Error sending credentials:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!endTime) return 'En cours';
    const duration = new Date(endTime) - new Date(startTime);
    const minutes = Math.floor(duration / 60000);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  const getActionBadgeColor = (action) => {
    const colors = {
      'login': 'bg-green-100 text-green-700',
      'update_employe': 'bg-blue-100 text-blue-700',
      'update_prospect': 'bg-purple-100 text-purple-700',
      'update_client': 'bg-orange-100 text-orange-700',
      'create_rdv': 'bg-pink-100 text-pink-700',
      'delete_rdv': 'bg-red-100 text-red-700',
      'create_user': 'bg-indigo-100 text-indigo-700'
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  const getActionLabel = (action) => {
    const labels = {
      'login': 'Connexion',
      'update_employe': 'Modification employé',
      'update_prospect': 'Modification prospect',
      'update_client': 'Modification client',
      'create_rdv': 'Création RDV',
      'delete_rdv': 'Suppression RDV',
      'create_user': 'Création utilisateur'
    };
    return labels[action] || action;
  };

  const formatDurationFromMinutes = (minutes) => {
    if (!minutes) return 'En cours';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric'
    });
  };

  const getTimeConnectedOrDisconnected = (lastActivity, isConnected) => {
    if (!lastActivity) return 'Jamais connecté';
    
    const lastActivityDate = new Date(lastActivity);
    const now = new Date();
    const diffMs = now - lastActivityDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    let timeStr = '';
    if (diffMins < 1) timeStr = "À l'instant";
    else if (diffMins < 60) timeStr = `${diffMins} min`;
    else if (diffHours < 24) timeStr = `${diffHours}h`;
    else timeStr = `${diffDays}j`;
    
    if (isConnected) {
      return `Connecté depuis ${timeStr}`;
    } else {
      return `Déconnecté depuis ${timeStr}`;
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="admin">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Check if user is admin
  if (user.role !== 'admin') {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="admin">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès Refusé</h2>
            <p className="text-gray-600">Seul l'administrateur peut accéder à cette page.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="admin">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Settings className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Administration</h1>
              <p className="text-gray-600">Gestion des utilisateurs et des accès</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              className={`gap-2 ${showAccountsManagement ? 'bg-orange-600 hover:bg-orange-700' : 'bg-orange-500 hover:bg-orange-600'}`}
              onClick={handleToggleAccountsManagement}
              data-testid="btn-toggle-accounts-management"
            >
              <Key className="h-4 w-4" />
              {showAccountsManagement ? 'Masquer comptes' : 'Gestion des Comptes'}
            </Button>
            <Button 
              className={`gap-2 ${showEmployeeStatus ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'}`}
              onClick={handleToggleEmployeeStatus}
              data-testid="btn-toggle-employee-status"
            >
              <Activity className="h-4 w-4" />
              {showEmployeeStatus ? 'Masquer état' : 'État de connexion'}
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              onClick={() => setShowCreateModal(true)}
              data-testid="btn-open-create-user"
            >
              <Plus className="h-4 w-4" />
              Créer identifiant
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 gap-2"
              onClick={openDefineModulesModal}
              data-testid="btn-open-define-modules"
            >
              <Grid3x3 className="h-4 w-4" />
              Définir les modules
            </Button>
            <Button
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50 gap-2"
              onClick={handleForceSeedEmplacements}
              data-testid="btn-force-seed-emplacements"
              title="Vérifier & ajouter automatiquement les 2 emplacements manquants (Devant l'hôtel Lévis, Devant l'hôpital général)"
            >
              <Send className="h-4 w-4" />
              Sync 10 emplacements déroulants
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card 
            className="bg-gradient-to-br from-blue-50 to-white border-blue-200 cursor-pointer hover:shadow-xl transition-shadow"
            onClick={handleShowUsersList}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">Utilisateurs Totaux</div>
                  <div className="text-4xl font-bold text-blue-600">{users.length}</div>
                  <div className="text-xs text-blue-600 mt-2 font-medium">Cliquez pour voir la liste</div>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">Admins / Investisseurs</div>
                  <div className="text-4xl font-bold text-green-600">
                    {users.filter(u => u.role === 'admin' || u.role === 'investisseur').length}
                  </div>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">Activités (24h)</div>
                  <div className="text-4xl font-bold text-purple-600">{activityLogs.length}</div>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Activity className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Accounts Management Section */}
        {showAccountsManagement && (
          <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <Key className="h-6 w-6" />
                Gestion des Comptes Employés
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                Créez et gérez les identifiants de connexion de vos employés. Le mot de passe par défaut est <code className="bg-orange-100 px-2 py-1 rounded text-orange-700 font-mono">sesam123</code>
              </p>
            </CardHeader>
            <CardContent>
              {loadingAccounts ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Chargement...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {employeesWithAccounts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p>Aucun employé trouvé</p>
                    </div>
                  ) : (
                    employeesWithAccounts.map((emp) => (
                      <div key={emp.id} className="flex items-center justify-between p-4 bg-white rounded-lg border-2 hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${
                            emp.has_account ? 'bg-green-100' : 'bg-gray-100'
                          }`}>
                            {emp.has_account ? (
                              <User className="h-7 w-7 text-green-600" />
                            ) : (
                              <UserPlus className="h-7 w-7 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{emp.prenom} {emp.nom}</h3>
                              {emp.has_account ? (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  ✓ Compte actif
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600 border-gray-200">
                                  Pas de compte
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{emp.poste} • {emp.departement}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="h-4 w-4 text-gray-400" />
                                <span className="text-gray-700 font-mono">{emp.email}</span>
                                {emp.has_account && (
                                  <button
                                    onClick={() => copyToClipboard(emp.email)}
                                    className="text-blue-600 hover:text-blue-700 ml-1"
                                    title="Copier l'email"
                                  >
                                    📋
                                  </button>
                                )}
                              </div>
                            </div>
                            {emp.has_account && (
                              <div className="flex items-center gap-2 mt-2">
                                <Key className="h-4 w-4 text-gray-400" />
                                <code className="text-sm bg-gray-100 px-3 py-1 rounded font-mono">sesam123</code>
                                <button
                                  onClick={() => copyToClipboard('sesam123')}
                                  className="text-blue-600 hover:text-blue-700"
                                  title="Copier le mot de passe"
                                >
                                  📋
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {emp.has_account ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-200 text-green-600 hover:bg-green-50"
                                onClick={() => handleSendCredentials(emp.id)}
                              >
                                <Mail className="h-4 w-4 mr-1" />
                                Envoyer par mail
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-200 text-blue-600 hover:bg-blue-50"
                                onClick={() => handleResetPassword(emp.id)}
                              >
                                <Key className="h-4 w-4 mr-1" />
                                Réinitialiser
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteAccount(emp.id)}
                                data-testid={`btn-delete-account-${emp.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Supprimer
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className={emp.is_blocked
                                  ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                                  : 'border-amber-200 text-amber-700 hover:bg-amber-50'}
                                onClick={() => handleToggleBlock(emp.user_id, emp.is_blocked)}
                                data-testid={`btn-toggle-block-${emp.id}`}
                              >
                                {emp.is_blocked
                                  ? (<><Unlock className="h-4 w-4 mr-1" />Débloquer</>)
                                  : (<><Lock className="h-4 w-4 mr-1" />Bloquer</>)}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-purple-200 text-purple-700 hover:bg-purple-50"
                                onClick={() => openModulesModal(emp)}
                                data-testid={`btn-set-modules-${emp.id}`}
                              >
                                <Grid3x3 className="h-4 w-4 mr-1" />
                                Modules {emp.modules_allowed?.length > 0 ? `(${emp.modules_allowed.length})` : ''}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-slate-200 text-slate-700 hover:bg-slate-100"
                                onClick={() => handleForceLogout(emp.user_id)}
                                data-testid={`btn-force-logout-${emp.id}`}
                              >
                                <ShieldOff className="h-4 w-4 mr-1" />
                                Déconnecter
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleCreateAccount(emp.id)}
                            >
                              <UserPlus className="h-4 w-4 mr-1" />
                              Créer compte
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Utilisateurs du CRM
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      u.role === 'admin' ? 'bg-indigo-100' : 
                      u.role === 'investisseur' ? 'bg-green-100' : 
                      'bg-blue-100'
                    }`}>
                      {u.role === 'admin' ? (
                        <Shield className="h-6 w-6 text-indigo-600" />
                      ) : u.role === 'investisseur' ? (
                        <DollarSign className="h-6 w-6 text-green-600" />
                      ) : (
                        <User className="h-6 w-6 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{u.name}</div>
                      <div className="text-sm text-gray-600">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={
                      u.role === 'admin' ? 'bg-indigo-100 text-indigo-700' : 
                      u.role === 'investisseur' ? 'bg-green-100 text-green-700' : 
                      u.role === 'technicien' ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }>
                      {u.role === 'admin' ? 'Administrateur' : 
                       u.role === 'investisseur' ? 'Investisseur' : 
                       u.role === 'technicien' ? 'Technicien' :
                       'Employé'}
                    </Badge>
                    {u.last_login && (
                      <div className="text-xs text-gray-500">
                        Dernière connexion: {new Date(u.last_login).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                    {u.role === 'employee' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2"
                        onClick={() => handleSendCredentials(u.email, u.name)}
                      >
                        <Send className="h-3 w-3" />
                        Envoyer identifiants
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteUserFromStatus({ user_id: u.id, email: u.email, name: u.name })}
                      data-testid={`btn-delete-user-total-${u.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Activity Logs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Journal d'activité
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {activityLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Aucune activité enregistrée</p>
                </div>
              ) : (
                activityLogs.map((log, index) => (
                  <div key={index} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Activity className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{log.user_name}</span>
                          <Badge className={getActionBadgeColor(log.action)}>
                            {getActionLabel(log.action)}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-600 truncate">{log.details}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                      <Clock className="h-3 w-3" />
                      {new Date(log.timestamp).toLocaleString('fr-FR')}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Employee Status Section - NEW */}
        {showEmployeeStatus && (
          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-6 w-6 text-purple-600" />
                  État de connexion des employés
                </div>
                <div className="flex items-center gap-4">
                  <Badge className="bg-green-100 text-green-700 gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    {employeesStatus.filter(e => e.is_connected).length} Connectés
                  </Badge>
                  <Badge className="bg-orange-100 text-orange-700 gap-2">
                    <div className="w-2 h-2 bg-orange-400 rounded-full" />
                    {employeesStatus.filter(e => !e.is_connected).length} Déconnectés
                  </Badge>
                  <Badge className="bg-purple-100 text-purple-700">
                    {employeesStatus.length} Total
                  </Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Full Employee List */}
              <div className="space-y-3">
                {employeesStatus.map((emp) => (
                  <div 
                    key={emp.id} 
                    onClick={() => handleEmployeeClick(emp)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-lg ${
                      selectedEmployee?.id === emp.id 
                        ? 'border-purple-400 bg-purple-50 shadow-md' 
                        : 'border-gray-200 hover:border-purple-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        {/* Status Button - Large and Clear */}
                        <div className={`flex items-center justify-center w-20 h-20 rounded-xl ${
                          emp.is_connected 
                            ? 'bg-gradient-to-br from-green-400 to-green-600 shadow-lg shadow-green-200' 
                            : 'bg-gradient-to-br from-orange-400 to-orange-600 shadow-lg shadow-orange-200'
                        }`}>
                          {emp.is_connected ? (
                            <div className="flex flex-col items-center">
                              <div className="w-4 h-4 bg-white rounded-full animate-pulse mb-1" />
                              <span className="text-white text-xs font-bold">ON</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <div className="w-4 h-4 bg-white rounded-full mb-1" />
                              <span className="text-white text-xs font-bold">OFF</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Employee Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-lg text-gray-900">{emp.name}</span>
                            {emp.role === 'admin' && (
                              <Badge className="bg-indigo-100 text-indigo-700 text-xs">Admin</Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">{emp.email}</div>
                          
                          {/* Connection Time */}
                          <div className="flex items-center gap-2 mt-2">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <span className={`text-sm font-semibold ${
                              emp.is_connected ? 'text-green-700' : 'text-orange-700'
                            }`}>
                              {getTimeConnectedOrDisconnected(emp.last_activity, emp.is_connected)}
                            </span>
                          </div>
                          
                          <div className="text-xs text-gray-500 mt-1">
                            {emp.activity_count_30d} activités sur les 30 derniers jours
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Button */}
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={emp.is_connected ? 'bg-green-100 text-green-700 px-3 py-1' : 'bg-orange-100 text-orange-700 px-3 py-1'}>
                          {emp.is_connected ? '🟢 Connecté' : '🟠 Déconnecté'}
                        </Badge>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" className="gap-2" data-testid={`btn-view-activities-${emp.id}`}>
                            <Activity className="h-3 w-3" />
                            Voir activités
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-200 text-red-600 hover:bg-red-50 gap-1"
                            onClick={(e) => { e.stopPropagation(); handleDeleteUserFromStatus(emp); }}
                            data-testid={`btn-delete-user-status-${emp.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Activity Details - Shown when employee is selected */}
              {selectedEmployee && (
                <div className="mt-6 space-y-4">
                  {loadingActivity ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                    </div>
                  ) : employeeActivity ? (
                    <div className="space-y-4">
                      {/* Employee Header */}
                      <div className="bg-gradient-to-r from-purple-100 to-indigo-100 p-4 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                            <User className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900">{employeeActivity.employee.name}</h3>
                            <p className="text-sm text-gray-600">{employeeActivity.employee.email}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3">
                          <div className="bg-white/60 p-2 rounded text-center">
                            <div className="text-2xl font-bold text-purple-600">{employeeActivity.summary.total_sessions}</div>
                            <div className="text-xs text-gray-600">Sessions</div>
                          </div>
                          <div className="bg-white/60 p-2 rounded text-center">
                            <div className="text-2xl font-bold text-indigo-600">{employeeActivity.summary.total_activities}</div>
                            <div className="text-xs text-gray-600">Activités</div>
                          </div>
                          <div className="bg-white/60 p-2 rounded text-center">
                            <div className="text-2xl font-bold text-blue-600">{employeeActivity.summary.period_days}</div>
                            <div className="text-xs text-gray-600">Jours</div>
                          </div>
                        </div>
                      </div>

                      {/* Sessions */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <LogIn className="h-4 w-4" />
                          Sessions de connexion
                        </h4>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {employeeActivity.sessions.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-4">Aucune session</p>
                          ) : (
                            employeeActivity.sessions.map((session, idx) => (
                              <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <LogIn className="h-4 w-4 text-green-600" />
                                      <span className="text-sm font-medium text-gray-700">
                                        {formatDateTime(session.login_time)}
                                      </span>
                                    </div>
                                    {session.logout_time && (
                                      <div className="flex items-center gap-2">
                                        <LogOut className="h-4 w-4 text-orange-600" />
                                        <span className="text-sm text-gray-600">
                                          {formatDateTime(session.logout_time)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <Badge className={session.is_current ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                                    {session.is_current ? 'En cours' : formatDurationFromMinutes(session.duration_minutes)}
                                  </Badge>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Activities */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Activity className="h-4 w-4" />
                          Activités récentes
                        </h4>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {employeeActivity.activities.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-4">Aucune activité</p>
                          ) : (
                            employeeActivity.activities.map((activity, idx) => (
                              <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-1">
                                  <Badge className={getActionBadgeColor(activity.action)}>
                                    {getActionLabel(activity.action)}
                                  </Badge>
                                  <span className="text-xs text-gray-500">
                                    {formatDate(activity.timestamp)}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 mt-1">{activity.details}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatDateTime(activity.timestamp)}
                                </p>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-6 w-6 text-blue-600" />
              Créer un identifiant
            </DialogTitle>
            <DialogDescription>
              Créez un nouvel identifiant de connexion avec un rôle spécifique
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900 font-medium mb-2">📋 Informations :</p>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li><strong>Employé</strong> : Accès aux modules que vous cochez ci-dessous (tous possibles sauf Admin)</li>
                <li><strong>Admin</strong> : Tous les droits + gestion des utilisateurs</li>
                <li><strong>Investisseur</strong> : Accès limité au tableau de bord uniquement</li>
              </ul>
            </div>

            {/* Role Selector */}
            <div>
              <Label className="text-base font-semibold mb-2 block">Type de connexion *</Label>
              <Select 
                value={newUser.role} 
                onValueChange={(value) => setNewUser({...newUser, role: value})}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Sélectionnez un rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-600" />
                      <span>Employé</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-indigo-600" />
                      <span>Admin</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="investisseur">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      <span>Investisseur</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Manual Form for Direct Account Creation */}
            <div className="space-y-3 border-t pt-4">
              <div>
                <Label htmlFor="name">Nom complet *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Ex: Jean Dupont"
                  value={newUser.employeId}
                  onChange={(e) => setNewUser({...newUser, employeId: e.target.value})}
                  data-testid="input-new-user-name"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Ex: jean.dupont@sesam.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  data-testid="input-new-user-email"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Mot de passe *</Label>
                <Input
                  id="password"
                  type="text"
                  placeholder="Ex: sesam123"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  data-testid="input-new-user-password"
                />
              </div>
            </div>

            {newUser.role !== 'admin' && (
              <div className="border-t pt-4" data-testid="create-user-modules">
                <Label className="text-base font-semibold mb-1 block">Modules autorisés</Label>
                <p className="text-xs text-slate-500 mb-2">Cochez les modules visibles par cet utilisateur. Aucun module coché = accès par défaut du rôle.</p>
                <ModuleChecklist
                  selected={newUser.modules || []}
                  onChange={(mods) => setNewUser({ ...newUser, modules: mods })}
                  prefix="create-module-check"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => {
                setShowCreateModal(false);
                setNewUser({
                  employeId: '',
                  email: '',
                  password: '',
                  role: 'employee'
                });
              }}>
                Annuler
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleCreateUser}
                data-testid="btn-submit-create-user"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Créer l'identifiant
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Users List Modal - NEW */}
      <Dialog open={showUsersListModal} onOpenChange={setShowUsersListModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Users className="h-6 w-6 text-blue-600" />
              Liste de tous les employés
            </DialogTitle>
            <DialogDescription>
              Statut de connexion et durée pour aujourd'hui
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg text-center border-2 border-blue-200">
                <div className="text-3xl font-bold text-blue-600">{employeesStatus.length}</div>
                <div className="text-sm text-gray-600">Total</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg text-center border-2 border-green-200">
                <div className="text-3xl font-bold text-green-600">
                  {employeesStatus.filter(e => e.is_connected).length}
                </div>
                <div className="text-sm text-gray-600">Connectés</div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg text-center border-2 border-orange-200">
                <div className="text-3xl font-bold text-orange-600">
                  {employeesStatus.filter(e => !e.is_connected).length}
                </div>
                <div className="text-sm text-gray-600">Déconnectés</div>
              </div>
            </div>

            {/* Employee List */}
            <div className="space-y-3">
              {employeesStatus.map((emp) => (
                <div 
                  key={emp.id}
                  className="flex items-center justify-between p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Status Button */}
                    <div className={`flex items-center justify-center w-16 h-16 rounded-xl ${
                      emp.is_connected 
                        ? 'bg-gradient-to-br from-green-400 to-green-600 shadow-lg shadow-green-200' 
                        : 'bg-gradient-to-br from-orange-400 to-orange-600 shadow-lg shadow-orange-200'
                    }`}>
                      {emp.is_connected ? (
                        <div className="flex flex-col items-center">
                          <div className="w-3 h-3 bg-white rounded-full animate-pulse mb-1" />
                          <span className="text-white text-xs font-bold">ON</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <div className="w-3 h-3 bg-white rounded-full mb-1" />
                          <span className="text-white text-xs font-bold">OFF</span>
                        </div>
                      )}
                    </div>

                    {/* Employee Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-900">{emp.name}</span>
                        {emp.role === 'admin' && (
                          <Badge className="bg-indigo-100 text-indigo-700 text-xs">Admin</Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">{emp.email}</div>
                      
                      {/* Daily Connection Time */}
                      <div className="flex items-center gap-2 mt-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className={`text-sm font-semibold ${
                          emp.daily_connection_minutes > 0 ? 'text-blue-700' : 'text-gray-500'
                        }`}>
                          {formatDailyConnectionTime(emp.daily_connection_minutes)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <Badge className={emp.is_connected ? 'bg-green-100 text-green-700 px-4 py-2' : 'bg-orange-100 text-orange-700 px-4 py-2'}>
                    {emp.is_connected ? '🟢 Connecté' : '🟠 Déconnecté'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credentials Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Key className="h-6 w-6" />
              Identifiants Créés
            </DialogTitle>
            <DialogDescription>
              Transmettez ces identifiants à l'employé. Il pourra se connecter au CRM avec ces informations.
            </DialogDescription>
          </DialogHeader>
          {selectedCredentials && (
            <div className="space-y-4 mt-4">
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 mb-3">🎉 Compte créé pour :</h3>
                <p className="text-lg font-bold text-green-700 mb-4">{selectedCredentials.name}</p>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1">Email</label>
                    <div className="flex items-center gap-2 bg-white p-3 rounded border border-green-200">
                      <code className="flex-1 font-mono text-sm">{selectedCredentials.email}</code>
                      <button
                        onClick={() => copyToClipboard(selectedCredentials.email)}
                        className="text-blue-600 hover:text-blue-700 px-2"
                        title="Copier"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-600 block mb-1">Mot de passe</label>
                    <div className="flex items-center gap-2 bg-white p-3 rounded border border-green-200">
                      <code className="flex-1 font-mono text-sm font-bold">{selectedCredentials.password}</code>
                      <button
                        onClick={() => copyToClipboard(selectedCredentials.password)}
                        className="text-blue-600 hover:text-blue-700 px-2"
                        title="Copier"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>🔗 Lien du CRM:</strong> <a href={window.location.origin} className="underline font-mono" target="_blank" rel="noopener noreferrer">{window.location.origin}</a>
                </p>
              </div>
              
              <Button 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => {
                  copyToClipboard(`Email: ${selectedCredentials.email}\nMot de passe: ${selectedCredentials.password}\nLien: ${window.location.origin}`);
                  toast.success('Toutes les informations copiées !');
                }}
              >
                📋 Copier toutes les informations
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal : sélection d'un employé pour définir ses modules (raccourci top) */}
      <Dialog open={showDefineModulesPicker} onOpenChange={setShowDefineModulesPicker}>
        <DialogContent className="max-w-lg" data-testid="define-modules-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid3x3 className="h-5 w-5 text-purple-600" />
              Définir les modules d&apos;un employé
            </DialogTitle>
            <DialogDescription>
              Sélectionnez l&apos;employé dont vous souhaitez configurer les modules visibles.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto space-y-2">
            {employeesWithAccounts.filter(e => e.has_account).length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-8">
                Aucun employé avec un compte utilisateur. Créez d&apos;abord un identifiant.
              </div>
            ) : (
              employeesWithAccounts.filter(e => e.has_account).map((emp) => (
                <button
                  key={emp.id}
                  onClick={() => pickEmployeeForModules(emp)}
                  className="w-full text-left p-3 rounded-lg border border-slate-200 bg-white hover:bg-purple-50 hover:border-purple-300 transition flex items-center gap-3"
                  data-testid={`pick-employee-${emp.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {emp.prenom} {emp.nom}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{emp.email}</div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {(emp.modules_allowed?.length || 0)} module{(emp.modules_allowed?.length || 0) > 1 ? 's' : ''}
                  </Badge>
                  <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal : sélection des modules autorisés pour un utilisateur */}
      <Dialog open={!!modulesUserId} onOpenChange={(v) => !v && setModulesUserId(null)}>
        <DialogContent className="max-w-lg" data-testid="modules-permissions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid3x3 className="h-5 w-5 text-purple-600" />
              Modules autorisés pour {modulesUserEmail}
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les modules visibles pour cet utilisateur dans la barre latérale. Laissez tout vide pour appliquer les permissions par défaut du rôle.
            </DialogDescription>
          </DialogHeader>
          <ModuleChecklist selected={modulesSelection} onChange={setModulesSelection} />
          <DialogFooter className="flex items-center justify-between gap-2 pt-3">
            <div className="text-xs text-slate-500">
              {modulesSelection.length === 0
                ? 'Aucun module coché → permissions par défaut du rôle'
                : `${modulesSelection.length} module(s) sélectionné(s)`}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setModulesUserId(null)}>Annuler</Button>
              <Button
                onClick={saveModules}
                disabled={modulesSaving}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="btn-save-modules"
              >
                {modulesSaving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
